<div
    *ngIf="viewModel | async as viewModel"
    class="mx-auto vstack gap-3"
>
    @if (isCancelling) {
        <p class="px-4 mb-0" role="status">Please wait…</p>
    }
    @if (cancellationError) {
        <p class="px-4 mb-0 text-danger" role="alert">{{ cancellationError }}</p>
    }
    @let grouped = viewModel.additionalBookings.length > 0;
    @if (!grouped || viewModel.hasSharedStatus) {
        <ng-container *ngTemplateOutlet="bookingStatus; context: { viewModel }" />
    }
    @if (grouped) {
        <header class="px-4 pt-3 vstack gap-2" data-testid="booking-group-overview">
            <h2 class="h4 fw-semibold mb-0">{{ viewModel.details.venueName }}</h2>
            <p class="mb-0">
                {{ viewModel.details.partySize | i18nPlural : partySizePluralMapping }} · {{ viewModel.details.bookingName }}
                @if (viewModel.hasSharedDate) {
                    <time class="d-block" [attr.datetime]="viewModel.details.dateTime.toISOString()" data-testid="booking-date-time">{{ viewModel.details.dateTime | date:'fullDate' }}</time>
                }
            </p>
            <span>{{ viewModel.details.venueAddress | addressString }}</span>
            <ng-container *ngTemplateOutlet="venueInformation; context: { $implicit: viewModel.details, grouped: true }" />
        </header>
    }
    <div [attr.role]="grouped ? 'list' : null" [attr.aria-label]="grouped ? 'Your bookings' : null">
    @for (booking of [viewModel].concat(viewModel.additionalBookings); track booking.details.bookingId) {
        <ng-container *ngTemplateOutlet="bookingRow; context: { viewModel: booking, grouped, sharedStatus: viewModel.hasSharedStatus, sharedDate: viewModel.hasSharedDate }" />
    }
    </div>
    @if (grouped) {
        <footer class="px-4 pb-4 vstack gap-2">
            @if (viewModel.showGetDirectionsButton) {
                <button class="btn btn-outline-dark" type="button" (click)="getDirectionsClicked(viewModel)">Get Directions</button>
            }
            @if (viewModel.showBookAgainButton) {
                <button class="btn btn-primary" type="button" (click)="bookAgainClicked(viewModel)">Make Another Booking</button>
            }
        </footer>
    }
</div>

<ng-template #bookingStatus let-viewModel="viewModel">
    <div class="mt-2 px-3 px-sm-5 py-sm-1 text-center">
        <ng-container
            *ngIf="viewModel.showCancellationStatus"
        >
            <div class="d-flex flex-row flex-sm-column gap-3 gap-sm-2 justify-content-center align-items-center">
                <h2 class="m-0 text-success">
                    <i class="bi bi-check-circle"></i>
                </h2>
                <h3 class="m-0 fw-semibold">Booking Cancelled</h3>
            </div>
        </ng-container>
        <ng-container
            *ngIf="viewModel.showBookingConfirmedStatus"
        >
            <div
                class="d-flex flex-row flex-sm-column gap-3 gap-sm-2 justify-content-center align-items-center"
                data-testid="booking-confirmed-status"
            >
                <h2 class="m-0 text-success">
                    <i class="bi bi-check-circle"></i>
                </h2>
                <h3 class="m-0 fw-semibold">Booking Confirmed</h3>
            </div>
        </ng-container>
        <div
            *ngIf="viewModel.showBookingRequestedStatus"
            class="vstack gap-2"
        >
            <div class="d-flex flex-row flex-sm-column gap-3 gap-sm-2 justify-content-center align-items-center">
                <img
                    class="d-block d-sm-none"
                    ngSrc="assets/clock/small_orange.svg"
                    height="24"
                    width="24"
                    alt="Requested"
                >
                <img
                    class="d-none d-sm-block"
                    ngSrc="assets/clock/large_orange.svg"
                    height="48"
                    width="48"
                    alt="Requested"
                >
                <h3 class="m-0 fw-semibold">Booking Requested</h3>
            </div>
            <span class="text-muted">
                We will contact you by email shortly to confirm your booking.
            </span>
        </div>
        @if (viewModel.showBookingRejectedStatus) {
            <div class="vstack gap-2">
                <div class="d-flex flex-row flex-sm-column gap-3 gap-sm-2 justify-content-center align-items-center">
                    <h2 class="m-0 text-danger">
                        <i class="bi bi-x-circle"></i>
                    </h2>
                    <h3 class="m-0 fw-semibold">Booking Request Declined</h3>
                </div>
                <span class="text-muted">
                    We're sorry to inform you that your booking request has not been accepted.
                </span>
            </div>
        }
        <div
            *ngIf="viewModel.showPendingDepositStatus"
            class="vstack gap-2"
        >
            <div class="d-flex flex-row flex-sm-column gap-3 gap-sm-2 justify-content-center align-items-center">
                <img
                    class="d-block d-sm-none"
                    ngSrc="assets/clock/small_orange.svg"
                    height="24"
                    width="24"
                    alt="Requested"
                >
                <img
                    class="d-none d-sm-block"
                    ngSrc="assets/clock/large_orange.svg"
                    height="48"
                    width="48"
                    alt="Requested"
                >
                <h3 class="m-0 fw-semibold">Pending Payment</h3>
            </div>
            <span
                *ngIf="viewModel.details.depositCharge"
                class="text-muted"
            >
                We require a {{ viewModel.paymentTerminology.lowercaseLabel }} of
                <span class="fw-bold">
                    {{ viewModel.details.depositCharge.toFormat() }}
                </span>
                to book your table.
                @if (viewModel.details.pendingPaymentExpiryDateTime) {
                    <br/>
                    Please confirm by
                    {{ viewModel.details.pendingPaymentExpiryDateTime | date :'short' }}
                    or your booking will be cancelled.
                }
            </span>
        </div>
        <div
            *ngIf="viewModel.showPendingCardDetailsStatus"
            class="vstack gap-2"
        >
            <div class="d-flex flex-row flex-sm-column gap-3 gap-sm-2 justify-content-center align-items-center">
                <img
                    class="d-block d-sm-none"
                    ngSrc="assets/clock/small_orange.svg"
                    height="24"
                    width="24"
                    alt="Requested"
                >
                <img
                    class="d-none d-sm-block"
                    ngSrc="assets/clock/large_orange.svg"
                    height="48"
                    width="48"
                    alt="Requested"
                >
                <h3 class="m-0 fw-semibold">Pending Card Details</h3>
            </div>
            <span
                *ngIf="viewModel.details.cancellationCharge"
                class="text-muted"
            >
                We require a card to secure your booking.
                @if (viewModel.details.pendingPaymentExpiryDateTime) {
                    <br/>
                    Please confirm by
                    {{ viewModel.details.pendingPaymentExpiryDateTime | date :'short' }}
                    or your booking will be cancelled.
                }
            </span>
            <span
                *ngIf="viewModel.details.cancellationCharge"
                class="text-muted"
            >
                Cancelling after {{ viewModel.details.cancellationChargeCutOffTime | date:'short' }}
                or not showing up for your booking may incur a cancellation fee of
                <span class="fw-bold">
                    {{ viewModel.details.cancellationCharge.toFormat() }}.
                </span>
            </span>
        </div>
        <ng-container
            *ngIf="viewModel.showReminderConfirmation"
        >
            <div class="d-flex flex-row flex-sm-column gap-3 gap-sm-2 justify-content-center align-items-center">
                <h2 class="m-0 text-success display-4">
                    <i class="bi bi-check-circle"></i>
                </h2>
                <h3 class="m-0 fw-semibold">
                    Thanks!
                </h3>
            </div>
            <h3 class="text-muted">
                We can't wait to see you there.
            </h3>
        </ng-container>
    </div>
</ng-template>

<ng-template #bookingRow let-viewModel="viewModel" let-grouped="grouped" let-sharedStatus="sharedStatus" let-sharedDate="sharedDate">
    <div
        *ngIf="viewModel.details as details"
        class="bg-light rounded bg-white vstack"
        [class.booking-confirmation-row]="grouped"
        [attr.role]="grouped ? 'listitem' : null"
    >
        <div class="px-4 pt-2 pb-4 vstack gap-4">
            @if (grouped && !sharedStatus) {
                <ng-container *ngTemplateOutlet="bookingStatus; context: { viewModel }" />
            }
            <div class="vstack gap-2">
                <div class="vstack fs-5 content-list content-card p-2">
                    <span>
                        {{ viewModel.bookingTableLabel }} for <strong>{{ details.partySize | i18nPlural : partySizePluralMapping }}</strong>
                        @if (!grouped || !sharedDate) {
                            on
                            <strong
                                class="text-nowrap"
                                data-testid="booking-date-time"
                            >
                                {{ details.dateTime | date:'fullDate' }}</strong>,
                        } @else {
                            at
                        }
                        <strong
                            class="text-nowrap"
                            data-testid="booking-start-time"
                        >
                            {{ details.dateTime | date:'shortTime' }}
                        </strong>
                        @if (grouped) {
                            &ndash;
                            <strong class="text-nowrap" data-testid="booking-end-time">{{ details.endTime | date:'shortTime' }}</strong>
                        }
                    </span>
                    @if (!grouped) {
                    <div class="hstack gap-2">
                        <small class="text-muted">
                            Ends at
                        </small>
                        <span class="fw-bold text-muted">
                            ·
                        </span>
                        <small class="fw-semibold" data-testid="booking-end-time">
                            {{ details.endTime | date:'shortTime' }}
                        </small>
                    </div>
                    <div class="hstack gap-2">
                        <small class="text-muted">
                            Name
                        </small>
                        <span class="fw-bold text-muted">
                            ·
                        </span>
                        <small class="fw-semibold" data-testid="booking-name">
                            {{ details.bookingName }}
                        </small>
                    </div>
                    }
                    <div
                        *ngIf="details.areaName"
                        class="hstack gap-2"
                    >
                        <small class="text-muted">
                            Area
                        </small>
                        <span class="fw-bold text-muted">
                            ·
                        </span>
                        <small class="fw-semibold" data-testid="booking-name">
                            {{ details.areaName }}
                        </small>
                    </div>
                    <div
                        *ngIf="details.reasonName"
                        class="hstack gap-2"
                    >
                        <small class="text-muted">
                            Reason
                        </small>
                        <span class="fw-bold text-muted">
                            ·
                        </span>
                        <small class="fw-semibold">
                            {{ details.reasonName }}
                        </small>
                    </div>
                    <div
                        *ngIf="details.eventName"
                        class="hstack gap-2"
                    >
                        <small class="text-muted">
                            Event
                        </small>
                        <span class="fw-bold text-muted">
                            ·
                        </span>
                        <small class="fw-semibold">
                            {{ details.eventName }}
                        </small>
                    </div>
                    <div
                        *ngIf="viewModel.details.depositCharge"
                        class="hstack gap-2"
                    >
                        <small class="text-muted">
                            {{ viewModel.paymentTerminology.label }}
                        </small>
                        <span class="fw-bold text-muted">
                            ·
                        </span>
                        <small class="fw-semibold">
                            {{ viewModel.details.depositCharge | currencyUnit }}
                            <ng-container
                                *ngIf="viewModel.showDepositUnpaidStatus"
                            >
                                <span class="text-muted">
                                    (Unpaid)
                                </span>
                            </ng-container>
                            <ng-container
                                *ngIf="viewModel.showDepositPaidStatus"
                            >
                                <span class="text-muted">
                                    (Paid)
                                </span>
                            </ng-container>
                            <ng-container
                                *ngIf="viewModel.showDepositRefundedStatus"
                            >
                                <span class="text-muted">
                                    (Refunded)
                                </span>
                            </ng-container>
                        </small>
                        <small
                            *ngIf="viewModel.showDepositPaidSoFar && viewModel.details.depositPaidCharge"
                            class="text-muted"
                        >
                            Paid so far:
                            <span class="fw-semibold">
                                {{ viewModel.details.depositPaidCharge | currencyUnit }}
                            </span>
                        </small>
                    </div>
                    @if (details.order; as order) {
                        @if (order.lineItems.length > 0) {
                            @let payment = details.sale?.payment;
                            <section aria-label="Booking products" class="vstack gap-2 fs-6" data-testid="booking-order-summary">
                                <div class="d-flex align-items-center justify-content-between gap-3">
                                    <h5 class="mb-0">Your products</h5>
                                    @if (details.hasUnpaidOrder) {
                                        <span class="badge bg-warning-subtle text-warning-emphasis lh-sm">Unpaid</span>
                                    } @else if (payment?.datePaid && payment.amountRefunded === 0) {
                                        <span class="badge bg-success-subtle text-success-emphasis lh-sm">Paid</span>
                                    }
                                </div>
                                @for (item of order.lineItems; track item.id) {
                                    @let refunded = (payment?.amountRefundedForLine(item.id) ?? 0) > 0;
                                    <div class="d-flex justify-content-between gap-3" data-testid="booking-order-line">
                                        <span>{{ item.quantity }} &times; {{ item.name }}
                                            @for (modifier of item.modifiers; track $index) {
                                                <small class="d-block text-secondary">{{ modifier.name }}</small>
                                            }
                                            @if (order.isPaid && item.paidOnline) {
                                                <small class="d-block text-secondary">{{ null | currencyUnit : item.unitAmount() : order.currencyCode }} each</small>
                                                @if (details.sale && !details.sale.isLineRefunded(item.id)) {
                                                    <small class="d-block">
                                                        @if (item.customerRefundableUntil) { Refundable if you cancel before {{ item.customerRefundableUntil | date:'short' }} }
                                                        @else { Non-refundable }
                                                    </small>
                                                }
                                            }
                                        </span>
                                        @if (order.isPaid && item.paidOnline) {
                                            <span class="text-nowrap text-end">
                                                @if (refunded) {
                                                    <s class="text-secondary"><span class="visually-hidden">Originally </span>{{ null | currencyUnit : item.unitAmount() * item.quantity : order.currencyCode }}</s>
                                                    <span class="d-block text-danger"><span class="visually-hidden">Remaining paid </span>{{ null | currencyUnit : details.sale!.remainingLineAmount(item.id) : order.currencyCode }}</span>
                                                } @else {
                                                    {{ null | currencyUnit : item.unitAmount() * item.quantity : order.currencyCode }}
                                                }
                                            </span>
                                        }
                                    </div>
                                }
                                @if (order.isPaid) {
                                    <div class="d-flex justify-content-between gap-3 fw-semibold" data-testid="booking-order-total">
                                        <span>{{ payment?.datePaid ? 'Products paid' : 'Total' }}</span>
                                        <span class="text-nowrap text-end">
                                            @if (payment && payment.amountRefunded > 0) {
                                                <s class="text-secondary"><span class="visually-hidden">Originally </span>{{ null | currencyUnit : order.total() : order.currencyCode }}</s>
                                                <span class="d-block text-danger"><span class="visually-hidden">Remaining paid </span>{{ null | currencyUnit : payment.balance : order.currencyCode }}</span>
                                            } @else {
                                                {{ null | currencyUnit : order.total() : order.currencyCode }}
                                            }
                                        </span>
                                    </div>
                                }
                            </section>
                        }
                    }
                    @if (details.sale && details.cancellationChargeAmount !== null) {
                        <section class="fs-6 vstack gap-2" aria-label="Cancellation charge">
                            <h5 class="mb-0">Cancellation charge</h5>
                            @if (details.cancellationAmountCharged > 0) {
                                <span>Cancellation fee charged: <strong>{{ null | currencyUnit:details.cancellationAmountCharged:details.currencyCode }}</strong></span>
                            } @else {
                                <span>Card saved · No fee charged</span>
                                <small class="text-secondary">A separate fee of {{ null | currencyUnit:details.cancellationChargeAmount:details.currencyCode }} may apply for a late cancellation or no-show.</small>
                            }
                        </section>
                    }
                </div>
                <span
                    class="px-2"
                    data-testid="booking-venue-address"
                    *ngIf="!grouped"
                >
                    {{ details.venueAddress | addressString }}
                </span>
            </div>
            <ng-template
                *ngTemplateOutlet="diningInformation; context: { $implicit: details, viewModel: viewModel, grouped }"
            >
            </ng-template>
            <div class="vstack gap-3">
                @if (viewModel.details.docket) {
                    <div class="vstack gap-2 rounded-1 bg-light p-3">
                        <div class="fw-semibold">
                            Pre-Order Details
                        </div>
                        <div *ngIf="viewModel.details.docket">
                            <div class="mt-2">
                                <app-menu-choices-progress
                                [docket]="viewModel.details.docket"
                                [totalCovers]="viewModel.details.partySize"
                                >
                                </app-menu-choices-progress>
                            </div>
                        </div>
                        @if (viewModel.showFillMenuChoicesButton) {
                            @let linkTypes = menuLinkTypes$ | async;
                            <button
                                class="btn btn-outline-secondary hstack gap-2 justify-content-center"
                                type="button"
                                role="button"
                                (click)="fillMenuChoicesClicked(viewModel, linkTypes?.get(viewModel.details.docket.menuId))"
                                [disabled]="!linkTypes || viewModel.disableFillMenuChoicesButton"
                                [attr.data-testid]="linkTypes ? 'fill-menu-choices-button' : 'fill-menu-choices-button-loading'"
                            >
                                @if (!linkTypes) {
                                    <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                }
                                Complete your Pre-Order
                                @if (linkTypes) {
                                    <img
                                        ngSrc="assets/right/normal.svg"
                                        width="16"
                                        height="16"
                                        alt="Complete your Pre-Order"
                                    />
                                }
                            </button>
                        }
                    </div>
                }
                <div class="vstack gap-2">
                    @if (viewModel.showModifyBookingButton) {
                        <button
                            class="btn btn-outline-dark"
                            type="button"
                            role="button"
                            (click)="modifyBookingClicked(viewModel.details)"
                        >
                            Modify Booking
                        </button>
                    }
                    @if (viewModel.showCannotModifyBookingText) {
                        <span
                            class="text-muted"
                            data-testid="modify-booking-not-available"
                        >
                            Unfortunately, you cannot modify or cancel this booking online. Please get in touch with the venue for further assistance.
                        </span>
                    }
                    @if (viewModel.showViewBookingButton) {
                        <button
                            class="btn btn-outline-dark"
                            type="button"
                            role="button"
                            (click)="viewBookingClicked(viewModel.details)"
                        >
                            View Booking
                        </button>
                    }
                    <button
                        *ngIf="viewModel.showGetDirectionsButton && !grouped"
                        class="btn btn-outline-dark"
                        type="button"
                        role="button"
                        (click)="getDirectionsClicked(viewModel)"
                    >
                        Get Directions
                    </button>
                </div>
                <div class="vstack gap-2">
                    <button
                        *ngIf="viewModel.showBookAgainButton && !grouped"
                        class="btn btn-primary"
                        type="button"
                        role="button"
                        (click)="bookAgainClicked(viewModel)"
                    >
                        Make Another Booking
                    </button>
                    <button
                        *ngIf="viewModel.showSaveCardDetailsButton"
                        class="btn btn-info"
                        type="button"
                        role="button"
                        (click)="confirmWithCard(viewModel)"
                    >
                        Enter Card Details
                    </button>
                    <button
                        *ngIf="viewModel.showCancelButton"
                        class="btn btn-danger"
                        type="button"
                        role="button"
                        (click)="cancelBookingClicked(viewModel)"
                        [disabled]="isCancelling"
                    >
                        Cancel Booking
                    </button>
                    <button
                        *ngIf="viewModel.showPayDepositButton"
                        class="btn btn-info"
                        type="button"
                        role="button"
                        (click)="confirmWithCard(viewModel)"
                    >
                        {{ viewModel.details.hasUnpaidOrder ? 'Pay for Products' : viewModel.paymentTerminology.actionLabel }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</ng-template>

<ng-template
    #diningInformation
    let-details
    let-viewModel=viewModel
    let-grouped="grouped"
>
<div class="vstack gap-2">
    <div
        *ngIf="details.cancellationChargeCutOffTime"
        class="alert alert-warning"
    >
        <div
            class="text-muted fw-semibold"
        >
            If you cancel this booking after
            {{ details.cancellationChargeCutOffTime | date:'shortTime' }}
            on
            {{ details.cancellationChargeCutOffTime | date:'fullDate' }},
            you {{ details.cancellationChargeAutomatically ? 'will' : 'may' }} be charged a cancellation fee of
            {{ details.cancellationCharge | currencyUnit }}.
        </div>
    </div>
    @if (details.depositRefundPolicy) {
        <div class="alert alert-warning text-body">
            <div class="quill-flush">
                <quill-view
                [content]="details.depositRefundPolicy"
                >
            </quill-view>
        </div>
    </div>
    } @else {
        @if (viewModel.showPendingDepositStatus) {
            <div
                *ngIf="details.depositRefundCutOffTime"
                class="alert alert-warning"
            >
                <span>
                    Cancelling after {{ details.depositRefundCutOffTime | date :'short' }}
                    or not showing up for your booking will not be refunded.
                </span>
            </div>
            <div
                *ngIf="!details.depositRefundCutOffTime"
                class="alert alert-warning"
            >
                <span>
                    Cancellations or no-shows will not be refunded.
                </span>
            </div>
        } @else if (details.depositAmount) {
            <div
                *ngIf="details.depositRefundCutOffTime"
                class="alert alert-warning"
            >
                <div
                    class="text-muted fw-semibold"
                >
                    If you cancel this booking after
                    {{ details.depositRefundCutOffTime | date:'shortTime' }}
                    on
                    {{ details.depositRefundCutOffTime | date:'fullDate' }},
                    you will not be refunded your {{ viewModel.paymentTerminology.lowercaseLabel }}.
                </div>
            </div>
            <div
                *ngIf="!details.depositRefundCutOffTime"
                class="alert alert-warning"
            >
                <div
                    class="text-muted fw-semibold"
                >
                    If you cancel this booking, you will not be refunded your
                    {{ viewModel.paymentTerminology.lowercaseLabel }}.
                </div>
            </div>
        }
    }
    <div class="quill-flush text-muted">
        <quill-view
            *ngIf="details.reasonDiningInformation"
            [content]="details.reasonDiningInformation"
        >
        </quill-view>
    </div>
    @if (!grouped) {
        <ng-container *ngTemplateOutlet="venueInformation; context: { $implicit: details, grouped: false }" />
    }
</div>
</ng-template>

<ng-template #venueInformation let-details let-grouped="grouped">
    <div class="quill-flush text-muted">
        <quill-view
            *ngIf="details.venueDiningInformation"
            [content]="details.venueDiningInformation"
        >
        </quill-view>
    </div>
    <div
        *ngIf="grouped || (!details.venueDiningInformation && !details.reasonDiningInformation)"
        class="text-muted"
    >
    @if (!details.venueEmailAddress && !details.venuePhoneNumber) {
        If you have any questions about your booking,
        please contact {{ details.venueName }}.
    } @else if (details.venueEmailAddress && !details.venuePhoneNumber) {
        If you have any questions about your booking,
        please contact {{ details.venueName }}
        at
        <a
            [href]="'mailto:' + details.venueEmailAddress"
            class="text-decoration-none fw-bold text-info"
        >
            {{ details.venueEmailAddress }}
        </a>.
    } @else if (!details.venueEmailAddress && details.venuePhoneNumber) {
        If you have any questions about your booking,
        please contact {{ details.venueName }}
        at
        <a
            [href]="'tel:' + details.venuePhoneNumber"
            class="text-decoration-none fw-bold text-info"
        >
            {{ details.venuePhoneNumber }}
        </a>.
    } @else {
        If you have any questions about your booking,
        please contact {{ details.venueName }}
        at
        <a
            [href]="'mailto:' + details.venueEmailAddress"
            class="text-decoration-none fw-bold text-info"
        >
            {{ details.venueEmailAddress }}
        </a>
        or
        <a
            [href]="'tel:' + details.venuePhoneNumber"
            class="text-decoration-none fw-bold text-info"
        >
            {{ details.venuePhoneNumber }}
        </a>.
    }
    </div>
</ng-template>
